/**
 * PetroNexus Operator — Design Tokens
 *
 * Canonical spec: /design/WEB_DESIGN.md
 *
 * Mobile shares Solar Gold accent and canvas values with web.
 * Everything else (type scale, primitives, density) is tuned for desktop.
 */

export const brand = {
  gold:       '#F0B820',
  goldLight:  '#FFD050',
  goldDark:   '#C49010',
  goldDeep:   '#9A7008',
  goldFill:   '#F0B820',
  goldFillHover: '#FFD050',
  goldFillPressed: '#C49010',
  goldSurfaceDark:  'rgba(240,184,32,0.12)',
  goldSurfaceLight: '#FDF6E8',
} as const;

export const lightBrand = {
  gold:       '#9A6200',
  goldLight:  '#8A5600',
  goldDark:   '#6F4300',
  goldDeep:   '#533000',
  goldFill:   '#F0B820',
  goldFillHover: '#FFD050',
  goldFillPressed: '#D49A00',
  goldSurfaceDark:  brand.goldSurfaceDark,
  // Transparent gold wash — never an opaque cream (#FFF8E3 read as mustard
  // at surface scale; killed on founder instruction 2026-06-10).
  goldSurfaceLight: 'rgba(240,184,32,0.10)',
} as const;

export const darkTokens = {
  // Surfaces
  bg:          '#06070A',
  panel:       '#0B0C10',
  card:        '#141516',
  cardHover:   '#1A1B1D',
  surfaceMuted: '#101217',
  surfaceRaised: '#181A1D',
  sidebarBg:   '#0B0C10',
  input:       '#090B0F',
  inputFocus:  '#0C0F15',
  overlay:     'rgba(6,7,10,0.85)',

  // Borders
  borderSubtle:  'rgba(255,255,255,0.05)',
  borderStd:     'rgba(255,255,255,0.08)',
  borderStrong:  'rgba(255,255,255,0.12)',
  divider:       'rgba(255,255,255,0.04)',

  // Text (white with opacity)
  textPrimary:     '#F7F8F8',
  textSecondary:   'rgba(247,248,248,0.70)',
  textTertiary:    'rgba(247,248,248,0.50)',
  textQuaternary:  'rgba(247,248,248,0.35)',

  // Brand accent surfaces
  brandSurface: brand.goldSurfaceDark,
  accentPrimary: brand.goldFill,
  accentSoftBg: 'rgba(240,184,32,0.12)',
  accentBorder: 'rgba(240,184,32,0.24)',
  accentText: brand.goldLight,
  warningBg: 'rgba(245,158,11,0.10)',
  warningText: '#FBBF24',
  warningBorder: 'rgba(245,158,11,0.24)',
  successBg: 'rgba(16,185,129,0.10)',
  successBorder: 'rgba(16,185,129,0.24)',
  dangerBg: 'rgba(239,68,68,0.10)',
  dangerBorder: 'rgba(239,68,68,0.24)',

  // Semantic
  success:  '#10B981',
  warning:  '#F59E0B',
  danger:   '#EF4444',
  info:     '#3B82F6',
  neutral:  'rgba(255,255,255,0.08)',

  // Elevation (inset highlight pattern, not box-shadow)
  shadowElev1: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 1px 0 rgba(0,0,0,0.3)',
  shadowElev2: 'inset 0 1px 0 rgba(255,255,255,0.08), 0 4px 16px rgba(0,0,0,0.4)',
  shadowCard:  'inset 0 1px 0 rgba(255,255,255,0.06), 0 1px 0 rgba(0,0,0,0.3)',
  glowBrand:   '0 0 0 3px rgba(240,184,32,0.22), 0 0 24px rgba(240,184,32,0.28)',

  /** Canvas atmosphere — faint radial gold wash from above the fold. */
  canvasGlow:
    'radial-gradient(ellipse 1400px 700px at 50% -250px, rgba(240,184,32,0.055) 0%, rgba(240,184,32,0.018) 38%, transparent 68%)',
} as const;

export const lightTokens = {
  // Surfaces — cool, clean enterprise canvas (Stripe/Linear class). Cards stay
  // bright white; the canvas is a barely-cool gray so white cards register.
  // No warm tints anywhere — locked by Kim 2026-06-10.
  bg:          '#FAFAFB',
  panel:       '#FFFFFF',
  card:        '#FFFFFF',
  cardHover:   '#F6F7F9',
  surfaceMuted: '#F2F4F6',
  surfaceRaised: '#FFFFFF',
  sidebarBg:   '#FFFFFF',
  input:       '#FFFFFF',
  inputFocus:  '#FFFFFF',
  overlay:     'rgba(14,17,22,0.32)',

  // Borders — crisp 1px definition. The previous 7% rings read as mush; cards
  // need a visible (but neutral) edge to feel engineered rather than washed.
  borderSubtle:  'rgba(16,18,28,0.10)',
  borderStd:     'rgba(16,18,28,0.14)',
  borderStrong:  'rgba(16,18,28,0.22)',
  divider:       'rgba(16,18,28,0.08)',

  // Text — rich ink, cool grays. Secondary text darkened for real hierarchy;
  // pale-gray-on-white was the main "washed out" complaint.
  textPrimary:     '#0E1116',
  textSecondary:   'rgba(14,17,22,0.66)',
  textTertiary:    'rgba(14,17,22,0.48)',
  textQuaternary:  'rgba(14,17,22,0.34)',

  // Brand accent surfaces — transparent gold washes only, never cream.
  brandSurface: lightBrand.goldSurfaceLight,
  accentPrimary: lightBrand.goldFill,
  accentSoftBg: 'rgba(240,184,32,0.11)',
  accentBorder: 'rgba(240,184,32,0.32)',
  accentText: lightBrand.gold,
  warningBg: 'rgba(180,83,9,0.08)',
  warningText: '#92400E',
  warningBorder: 'rgba(180,83,9,0.20)',
  successBg: 'rgba(13,148,136,0.08)',
  successBorder: 'rgba(13,148,136,0.20)',
  dangerBg: 'rgba(217,74,74,0.08)',
  dangerBorder: 'rgba(217,74,74,0.20)',

  // Semantic — restrained tones for white surfaces, not a page-wide tint.
  success:  '#0D9488',
  warning:  '#B45309',
  danger:   '#D94A4A',
  info:     '#2563EB',
  neutral:  'rgba(16,18,28,0.05)',

  // Elevation — layered Stripe-style: hairline ring + tight contact shadow +
  // soft ambient. This is what makes white-on-near-white feel premium.
  shadowElev1: '0 0 0 1px rgba(16,18,28,0.06), 0 1px 2px rgba(16,24,40,0.05), 0 1px 3px rgba(16,24,40,0.06)',
  shadowElev2: '0 0 0 1px rgba(16,18,28,0.07), 0 4px 12px rgba(16,24,40,0.06), 0 24px 48px -12px rgba(16,24,40,0.16)',
  shadowCard:  '0 0 0 1px rgba(16,18,28,0.06), 0 1px 2px rgba(16,24,40,0.04), 0 2px 8px rgba(16,24,40,0.05)',
  glowBrand:   '0 0 0 3px rgba(240,184,32,0.20), 0 10px 24px rgba(16,24,40,0.06)',

  /** Light canvas: barely-there structure, no obvious decorative blobs. */
  canvasGlow:
    'linear-gradient(180deg, rgba(255,255,255,0.66) 0%, rgba(250,250,251,0) 280px), repeating-linear-gradient(90deg, rgba(16,18,28,0.008) 0 1px, transparent 1px 72px), repeating-linear-gradient(180deg, rgba(16,18,28,0.007) 0 1px, transparent 1px 72px)',
} as const;

export type OperatorTokens = typeof darkTokens;

// Spacing — 4pt base
export const space = {
  s0: 0,
  s1: 4,
  s2: 8,
  s3: 12,
  s4: 16,
  s5: 24,
  s6: 32,
  s7: 48,
  s8: 64,
} as const;

// Radii — deliberately tight. Enterprise tools earn trust with precision;
// 12px+ "friendly" corners on data surfaces read consumer/template-grade.
// Cards sit at 8px, modals at 12px, chips at 4–6px (Linear/Stripe class).
export const radii = {
  r0: 0,
  r1: 4,
  r2: 6,
  r3: 8,
  r4: 12,
  r5: 16,
  rPill: 999,
} as const;

// Fonts
export const fonts = {
  sans: '"Inter Variable", "Inter", -apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
  mono: '"Geist Mono Variable", "Geist Mono", ui-monospace, "SF Mono", "Menlo", monospace',
} as const;

// Type scale — size, weight, lineHeight, letterSpacing
export const type = {
  display:      { size: 64, weight: 510, line: 1.00, tracking: -1.408 },
  titleXL:      { size: 48, weight: 510, line: 1.05, tracking: -1.056 },
  titleL:       { size: 32, weight: 510, line: 1.10, tracking: -0.704 },
  titleM:       { size: 24, weight: 510, line: 1.20, tracking: -0.288 },
  titleS:       { size: 20, weight: 590, line: 1.30, tracking: -0.240 },
  bodyL:        { size: 16, weight: 400, line: 1.50, tracking: 0 },
  body:         { size: 15, weight: 400, line: 1.50, tracking: -0.165 },
  bodyM:        { size: 15, weight: 510, line: 1.50, tracking: -0.165 },
  small:        { size: 13, weight: 400, line: 1.45, tracking: -0.13 },
  smallM:       { size: 13, weight: 510, line: 1.45, tracking: -0.13 },
  monoLabel:    { size: 11, weight: 510, line: 1.40, tracking:  0.8  },
  monoMeta:     { size: 12, weight: 400, line: 1.40, tracking:  0.3  },
  tiny:         { size: 10, weight: 510, line: 1.40, tracking:  0.8  },
} as const;

// Motion
export const motion = {
  durations: {
    fast:       150,
    base:       220,
    slow:       320,
    deliberate: 500,
  },
  easings: {
    standard:   'cubic-bezier(0.4, 0, 0.2, 1)',
    accelerate: 'cubic-bezier(0.4, 0, 1, 1)',
    decelerate: 'cubic-bezier(0, 0, 0.2, 1)',
  },
  springs: {
    subtle:   { damping: 20, stiffness: 300, mass: 0.8 },
    standard: { damping: 18, stiffness: 250, mass: 1.0 },
    bouncy:   { damping: 12, stiffness: 200, mass: 0.9 },
    stiff:    { damping: 25, stiffness: 400, mass: 1.0 },
  },
} as const;
